import React, { useEffect, useState } from "react";
import { jsPDF } from "jspdf";
import { api } from "@/lib/api";
import { PageHeader, Panel } from "@/components/portal/Shared";
import { EvidenceBadge } from "@/components/portal/EvidenceBadge";
import { toast } from "sonner";
import { Feather, Loader2, MapPin, ScrollText, History, GitCompareArrows, X, Share2, Copy, Download, Image as ImageIcon } from "lucide-react";

const INVITE_URL = "https://whatsmytribe.com";

const inviteMessage = (aff) =>
  `I discovered my tribal research affiliation on WhatsMyTribe.com — ${aff.primary_affiliation} ` +
  `(a research lead based on my maternal line in ${aff.location}). ` +
  `Start your own family history research and find your tribe: ${INVITE_URL}`;

function downloadShareCard(aff) {
  const W = 612, H = 792, cx = 46, cw = 520, cy = 96, ch = 600;
  const doc = new jsPDF({ unit: "pt", format: [W, H] });
  doc.setFillColor("#180D24"); doc.rect(0, 0, W, H, "F");
  doc.setFillColor("#FFFFFF"); doc.roundedRect(cx, cy, cw, ch, 20, 20, "F");
  // Header band
  doc.setFillColor("#2A1442"); doc.roundedRect(cx, cy, cw, 118, 20, 20, "F"); doc.rect(cx, cy + 70, cw, 48, "F");
  const mid = cx + cw / 2;
  doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor("#E7C965");
  doc.text("W H A T ' S   M Y   T R I B E", mid, cy + 40, { align: "center" });
  doc.setFont("times", "italic"); doc.setFontSize(12); doc.setTextColor("#CDBFE0");
  doc.text("My Tribal Affiliation — Research Lead", mid, cy + 62, { align: "center" });
  // Body
  let y = cy + 172;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor("#8F7220");
  doc.text("PRIMARY AFFILIATION", mid, y, { align: "center" }); y += 30;
  doc.setFont("times", "bold"); doc.setFontSize(24); doc.setTextColor("#59288E");
  doc.splitTextToSize(aff.primary_affiliation || "Undetermined", cw - 80).forEach((ln) => { doc.text(ln, mid, y, { align: "center" }); y += 26; });
  y += 8;
  doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.setTextColor("#4a4550");
  doc.text(doc.splitTextToSize(`Based on ${aff.basis_relation || "maternal ancestor"} ${aff.basis_ancestor || ""}`, cw - 90), mid, y, { align: "center" }); y += 18;
  doc.setTextColor("#1A171D");
  doc.text(doc.splitTextToSize(`${aff.location || ""}${aff.era ? " · " + aff.era : ""}`, cw - 90), mid, y, { align: "center" }); y += 26;
  if (typeof aff.confidence === "number") {
    doc.setFont("helvetica", "bold"); doc.setFontSize(10); doc.setTextColor("#8F7220");
    doc.text(`RESEARCH LEAD · Confidence ${aff.confidence}%`, mid, y, { align: "center" }); y += 26;
  }
  const tribes = (aff.prevalent_tribes || []).slice(0, 3).map((t) => t.name).filter(Boolean);
  if (tribes.length) {
    doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor("#8F7220");
    doc.text("PREVALENT IN THIS REGION", mid, y, { align: "center" }); y += 16;
    doc.setFont("times", "normal"); doc.setFontSize(11); doc.setTextColor("#3F1E68");
    doc.text(doc.splitTextToSize(tribes.join("  •  "), cw - 80), mid, y, { align: "center" }); y += 26;
  }
  // Invite footer
  const fy = cy + ch - 96;
  doc.setDrawColor("#E4DCD3"); doc.line(cx + 40, fy - 18, cx + cw - 40, fy - 18);
  doc.setFont("times", "bolditalic"); doc.setFontSize(15); doc.setTextColor("#180D24");
  doc.text("Discover your tribe.", mid, fy + 6, { align: "center" });
  doc.setFont("helvetica", "bold"); doc.setFontSize(13); doc.setTextColor("#59288E");
  doc.text("WhatsMyTribe.com", mid, fy + 28, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor("#8F7220");
  doc.text("Powered by ISelfLawAmMaster.com — a research lead, not proof of citizenship or enrollment.", mid, fy + 48, { align: "center" });
  doc.save("my-tribal-affiliation-card.pdf");
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function downloadShareCardPng(aff) {
  const W = 720, H = 920, cx = 56, cw = 608, cy = 110, ch = 700;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const mid = cx + cw / 2;
  ctx.fillStyle = "#180D24"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#FFFFFF"; roundRect(ctx, cx, cy, cw, ch, 24); ctx.fill();
  // header band
  ctx.save(); roundRect(ctx, cx, cy, cw, 140, 24); ctx.clip();
  ctx.fillStyle = "#2A1442"; ctx.fillRect(cx, cy, cw, 140); ctx.restore();
  ctx.textAlign = "center";
  ctx.fillStyle = "#E7C965"; ctx.font = "bold 15px Helvetica, Arial, sans-serif";
  ctx.fillText("W H A T ' S   M Y   T R I B E", mid, cy + 56);
  ctx.fillStyle = "#CDBFE0"; ctx.font = "italic 17px Georgia, serif";
  ctx.fillText("My Tribal Affiliation — Research Lead", mid, cy + 88);
  // body
  let y = cy + 200;
  ctx.fillStyle = "#8F7220"; ctx.font = "bold 13px Helvetica, Arial, sans-serif";
  ctx.fillText("PRIMARY AFFILIATION", mid, y); y += 42;
  ctx.fillStyle = "#59288E"; ctx.font = "bold 34px Georgia, serif";
  const words = (aff.primary_affiliation || "Undetermined").split(" ");
  let line = "";
  const lines = [];
  words.forEach((w) => {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > cw - 90 && line) { lines.push(line); line = w; } else line = test;
  });
  if (line) lines.push(line);
  lines.forEach((ln) => { ctx.fillText(ln, mid, y); y += 40; });
  y += 6;
  ctx.fillStyle = "#4a4550"; ctx.font = "15px Helvetica, Arial, sans-serif";
  ctx.fillText(`Based on ${aff.basis_relation || "maternal ancestor"} ${aff.basis_ancestor || ""}`.trim(), mid, y); y += 26;
  ctx.fillStyle = "#1A171D"; ctx.font = "15px Helvetica, Arial, sans-serif";
  ctx.fillText(`${aff.location || ""}${aff.era ? " · " + aff.era : ""}`, mid, y); y += 36;
  if (typeof aff.confidence === "number") {
    ctx.fillStyle = "#8F7220"; ctx.font = "bold 14px Helvetica, Arial, sans-serif";
    ctx.fillText(`RESEARCH LEAD · Confidence ${aff.confidence}%`, mid, y); y += 34;
  }
  const tribes = (aff.prevalent_tribes || []).slice(0, 3).map((t) => t.name).filter(Boolean);
  if (tribes.length) {
    ctx.fillStyle = "#8F7220"; ctx.font = "12px Helvetica, Arial, sans-serif";
    ctx.fillText("PREVALENT IN THIS REGION", mid, y); y += 24;
    ctx.fillStyle = "#3F1E68"; ctx.font = "15px Georgia, serif";
    ctx.fillText(tribes.join("   •   "), mid, y); y += 30;
  }
  // footer
  const fy = cy + ch - 110;
  ctx.strokeStyle = "#E4DCD3"; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(cx + 48, fy - 20); ctx.lineTo(cx + cw - 48, fy - 20); ctx.stroke();
  ctx.fillStyle = "#180D24"; ctx.font = "italic bold 20px Georgia, serif";
  ctx.fillText("Discover your tribe.", mid, fy + 10);
  ctx.fillStyle = "#59288E"; ctx.font = "bold 18px Helvetica, Arial, sans-serif";
  ctx.fillText("WhatsMyTribe.com", mid, fy + 40);
  ctx.fillStyle = "#8F7220"; ctx.font = "11px Helvetica, Arial, sans-serif";
  ctx.fillText("Powered by ISelfLawAmMaster.com — a research lead, not proof of citizenship.", mid, fy + 64);
  canvas.toBlob((blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "my-tribal-affiliation-card.png";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }, "image/png");
}

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

function ResultBody({ aff, compact }) {
  return (
    <>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-1">Primary Tribal Affiliation — Research Lead</div>
          <div className={`font-serif ${compact ? "text-xl" : "text-3xl"} font-bold text-[#59288E]`}>{aff.primary_affiliation}</div>
          <div className="mt-1 text-sm text-[#756D7E] flex items-center gap-1.5">
            <MapPin size={13} /> Based on {aff.basis_relation || "maternal ancestor"} <span className="font-medium text-[#3F1E68]">{aff.basis_ancestor}</span> — {aff.location}{aff.era ? ` (${aff.era})` : ""}
          </div>
        </div>
        <div className="text-right">
          <EvidenceBadge status={aff.evidence_status || "RESEARCH_LEAD"} size={compact ? "sm" : "lg"} />
          {typeof aff.confidence === "number" && <div className="mt-1 text-xs font-mono text-[#8F7220]">Confidence {aff.confidence}%</div>}
        </div>
      </div>
      {aff.rationale && <p className="mt-4 text-sm text-[#1A171D] leading-relaxed">{aff.rationale}</p>}
      {aff.prevalent_tribes?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-2">Tribes historically prevalent in this location</div>
          <div className={`grid ${compact ? "grid-cols-1" : "sm:grid-cols-2"} gap-2`}>
            {aff.prevalent_tribes.map((t, i) => (
              <div key={i} className="rounded-xl border border-[#E4DCD3] bg-[#FAF8F5] p-3">
                <div className="font-semibold text-[#180D24] text-sm">{t.name}</div>
                {t.note && <div className="text-xs text-[#756D7E] mt-0.5">{t.note}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {aff.records_to_pursue?.length > 0 && !compact && (
        <div className="mt-4">
          <div className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mb-2">Records to pursue for documentary proof</div>
          <ul className="space-y-1.5">
            {aff.records_to_pursue.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#1A171D]"><ScrollText size={14} className="text-[#D4AF37] shrink-0 mt-0.5" />{r}</li>
            ))}
          </ul>
        </div>
      )}
      {!compact && (
        <div className="mt-4 rounded-xl bg-[#FFFBEB] border border-[#F59E0B]/30 p-3 text-xs text-[#B45309]">
          {aff.disclaimer || "This is a location-based research lead only. It does not establish citizenship, enrollment or membership in any tribal nation — each nation sets its own requirements."}
        </div>
      )}
    </>
  );
}

export default function Affiliation({ onAffiliationComplete }) {
  const [loading, setLoading] = useState(false);
  const [aff, setAff] = useState(null);
  const [history, setHistory] = useState([]);
  const [compare, setCompare] = useState(false);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/tribal-affiliation/history");
      setHistory(data);
      if (!aff && data.length) setAff(data[0].result);
      if (data.length >= 2) { setCmpA((a) => a || data[0].id); setCmpB((b) => b || data[1].id); }
    } catch { /* ignore */ }
  };

  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, []);

  const analyze = async () => {
    setLoading(true); setAff(null);
    try {
      const { data } = await api.post("/tribal-affiliation");
      setAff(data);
      toast.success("Affiliation report generated — all Heritage Portal tabs are now unlocked!");
      loadHistory();
      if (onAffiliationComplete) onAffiliationComplete();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze affiliation");
    } finally { setLoading(false); }
  };

  const runById = (id) => history.find((h) => h.id === id)?.result;
  return (
    <div data-testid="module-tribal-affiliation">
      <PageHeader
        eyebrow="Understand • Matrilineal"
        title="Tribal Affiliation"
        subtitle="Generate a detailed research report of your primary tribal affiliation, based on your maternal great-grandmother's (or grandmother's) documented location and the tribes historically prevalent there."
        right={
          <button data-testid="run-affiliation-btn" onClick={analyze} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#482276] transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Feather size={16} className="text-[#E7C965]" />}
            Generate Affiliation Report
          </button>
        }
      />

      {!aff && !loading && history.length === 0 && (
        <Panel className="p-10 text-center text-[#756D7E]">
          <Feather size={30} className="text-[#D4AF37] mx-auto mb-3" />
          <p className="text-sm max-w-lg mx-auto">Click “Generate Affiliation Report”. We analyze your matrilineal line and the tribes prevalent to that location, drop a pin on your Historical Map, and open matching Tribal Research Paths.</p>
        </Panel>
      )}
      {loading && (
        <Panel className="p-16 text-center text-[#756D7E]"><Loader2 size={22} className="animate-spin text-[#59288E] mx-auto" /><div className="mt-3 font-mono text-sm">Analyzing your maternal line…</div></Panel>
      )}

      {aff && !compare && (
        <Panel className="p-6 hp-fade-up" data-testid="affiliation-result">
          <ResultBody aff={aff} />
          <div className="mt-5 pt-4 border-t border-[#E4DCD3] flex items-center gap-2 flex-wrap" data-testid="affiliation-share">
            <span className="text-xs uppercase font-mono tracking-wider text-[#8F7220] mr-1">Share your finding</span>
            <button data-testid="share-card-btn" onClick={() => { try { downloadShareCard(aff); } catch (e) { toast.error("Could not build the card"); } }}
              className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#180D24] px-4 py-2 text-sm font-bold hover:bg-[#E7C965] transition-colors">
              <Download size={15} /> Download PDF Card
            </button>
            <button data-testid="share-card-png-btn" onClick={() => { try { downloadShareCardPng(aff); } catch (e) { toast.error("Could not build the image"); } }}
              className="inline-flex items-center gap-2 rounded-full bg-[#2A1442] text-white px-4 py-2 text-sm font-bold hover:bg-[#482276] transition-colors">
              <ImageIcon size={15} className="text-[#E7C965]" /> Download PNG
            </button>
            <button data-testid="copy-invite-btn"
              onClick={async () => {
                const text = inviteMessage(aff);
                try { await navigator.clipboard.writeText(text); toast.success("Invite copied — share it with your family"); return; } catch { /* fallback below */ }
                try {
                  const ta = document.createElement("textarea"); ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
                  document.body.appendChild(ta); ta.focus(); ta.select();
                  const ok = document.execCommand("copy"); document.body.removeChild(ta);
                  if (ok) { toast.success("Invite copied — share it with your family"); return; }
                } catch { /* ignore */ }
                toast.error("Could not copy — visit whatsmytribe.com to share");
              }}
              className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] px-4 py-2 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
              <Copy size={15} /> Copy Invite
            </button>
            {typeof navigator !== "undefined" && navigator.share && (
              <button data-testid="native-share-btn"
                onClick={async () => { try { await navigator.share({ title: "My Tribal Affiliation", text: inviteMessage(aff), url: INVITE_URL }); } catch { /* cancelled */ } }}
                className="inline-flex items-center gap-2 rounded-full border border-[#E4DCD3] px-4 py-2 text-sm font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
                <Share2 size={15} /> Share
              </button>
            )}
          </div>
        </Panel>
      )}

      {compare && (
        <div data-testid="affiliation-compare">
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            {[[cmpA, setCmpA, "compare-select-a"], [cmpB, setCmpB, "compare-select-b"]].map(([val, setter, tid], i) => (
              <label key={i} className="block">
                <span className="text-xs uppercase font-mono tracking-wider text-[#8F7220]">Report {i === 0 ? "A" : "B"}</span>
                <select data-testid={tid} value={val} onChange={(e) => setter(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-[#E4DCD3] bg-white px-3 py-2.5 text-sm">
                  {history.map((h) => <option key={h.id} value={h.id}>{fmtDate(h.created_at)} — {h.result?.primary_affiliation}</option>)}
                </select>
              </label>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[cmpA, cmpB].map((id, i) => {
              const h = history.find((x) => x.id === id);
              const r = h?.result;
              return (
                <Panel key={i} className="p-5" data-testid={`compare-panel-${i}`}>
                  {r ? (
                    <>
                      <div className="text-xs font-mono text-[#8F7220] mb-3 pb-2 border-b border-[#E4DCD3]">{fmtDate(h.created_at)}{typeof r.confidence === "number" ? ` · Confidence ${r.confidence}%` : ""}</div>
                      <ResultBody aff={r} compact />
                    </>
                  ) : <div className="text-sm text-[#756D7E]">Select a report</div>}
                </Panel>
              );
            })}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <Panel className="p-6 mt-5" data-testid="affiliation-history">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-[#180D24] font-serif text-lg font-semibold"><History size={18} className="text-[#59288E]" /> Report History</div>
            {history.length >= 2 && (
              <button data-testid="toggle-compare-btn" onClick={() => setCompare((c) => !c)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E4DCD3] px-3 py-1.5 text-xs font-semibold text-[#59288E] hover:border-[#D4AF37] transition-colors">
                {compare ? <><X size={13} /> Close compare</> : <><GitCompareArrows size={13} /> Compare two</>}
              </button>
            )}
          </div>
          <div className="space-y-2">
            {history.map((h) => (
              <button key={h.id} data-testid={`history-item-${h.id}`} onClick={() => { setCompare(false); setAff(h.result); }}
                className="w-full text-left flex items-center justify-between gap-3 rounded-xl border border-[#E4DCD3] p-3 hover:border-[#D4AF37] transition-colors">
                <div>
                  <div className="font-semibold text-[#180D24] text-sm">{h.result?.primary_affiliation || "Affiliation lead"}</div>
                  <div className="text-xs text-[#756D7E]">{h.result?.basis_ancestor} — {h.result?.location}</div>
                </div>
                <div className="text-xs font-mono text-[#8F7220] shrink-0">{fmtDate(h.created_at)}</div>
              </button>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
